import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import "./Styles/InvUI.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShoppingCart, faUser, faPhone, faMapMarkerAlt, faCalendarAlt, faFileInvoice, faBox, faRupeeSign, faTag,
    faBalanceScale, faDollarSign, faCalculator, faExclamationTriangle, faPlus, faMinus, faTrash, faSave, faTimes, faTrashAlt,
    faSpinner, faCheckCircle, faExclamationCircle, faSearch, faBarcode, faQrcode, faCreditCard, faMoneyBill,
    faUniversity, faWallet, faPercent, faReceipt, faChevronDown, faChevronUp, faBoxes, faHistory, faUndo, faRedo,
    faPrint, faFilePdf, faDownload, faUpload, faEdit, faChartLine, faTimesCircle, faShoppingBag, faAddressCard,
    faIdCard, faKey, faLock, faUnlock, faEye, faEyeSlash, faStore, faEnvelope, faGlobe, faBuilding, faFileAlt,
    faTruck, faWeightHanging, faCubes, faLayerGroup, faToggleOn, faToggleOff, faCopy, faList
} from '@fortawesome/free-solid-svg-icons';

// ==============================================
// API Endpoints
// ==============================================
const API_BASE = 'https://ourzestapp.online/oz_backend/mechanics_module';
const SELL_API = `${API_BASE}/mechanics_sell.php`;
const CUSTOMER_API = `${API_BASE}/mechanics_customers.php`;
const PRODUCT_API = `${API_BASE}/mechanics_products.php`;

// ==============================================
// Helper function to parse BQuantity
// ==============================================
const parseBQuantityNumeric = (bquantityStr) => {
    if (!bquantityStr) return 0;
    const match = bquantityStr.match(/^([\d,\.]+)/);
    if (match) {
        return parseFloat(match[1].replace(/,/g, ''));
    }
    return 0;
};

// ==============================================
// Translation Utility
// ==============================================
const createSellTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_sell_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Sell Form");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_sell_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        const dict = {
            'ur': {
                'New Sale': 'نیا سیل',
                'Edit Sale': 'سیل میں ترمیم کریں',
                'Sale Details': 'سیل کی تفصیلات',
                'Sell Products': 'پروڈکٹس بیچیں',
                'Bill Number': 'بل نمبر',
                'Date': 'تاریخ',
                'Customer Details': 'کسٹمر کی تفصیلات',
                'Customer Name': 'کسٹمر کا نام',
                'Phone Number': 'فون نمبر',
                'Address': 'پتہ',
                'Search Products': 'پروڈکٹس تلاش کریں',
                'Product Name': 'پروڈکٹ',
                'Product Code': 'پروڈکٹ کوڈ',
                'Batch': 'بیچ',
                'Price': 'قیمت',
                'Quantity': 'مقدار',
                'Total': 'کل',
                'Remove': 'ہٹائیں',
                'Stock': 'اسٹاک',
                'Subtotal': 'ذیلی کل',
                'Tax': 'ٹیکس',
                'Discount': 'ڈسکاؤنٹ',
                'Grand Total': 'کل رقم',
                'Paid Amount': 'ادا کردہ رقم',
                'Balance': 'باقی رقم',
                'Payment Method': 'ادائیگی کا طریقہ',
                'Cash': 'نقد',
                'Card': 'کارڈ',
                'UPI': 'یوپی آئی',
                'Complete Sale': 'سیل مکمل کریں',
                'Update Sale': 'سیل اپ ڈیٹ کریں',
                'Clear': 'صاف کریں',
                'Save Draft': 'ڈرافٹ محفوظ کریں',
                'Drafts': 'ڈرافٹس',
                'Saved Drafts': 'محفوظ کردہ ڈرافٹس',
                'No drafts saved': 'کوئی ڈرافٹ محفوظ نہیں',
                'Draft loaded': 'ڈرافٹ لوڈ ہو گیا',
                'Draft cleared': 'ڈرافٹ صاف ہو گیا',
                'Clear Form': 'فارم صاف کریں',
                'View Drafts': 'ڈرافٹس دیکھیں',
                'Success': 'کامیابی',
                'Error': 'خرابی',
                'Warning': 'انتباہ',
                'receipt': 'رسید',
                'printReceipt': 'رسید پرنٹ کریں',
                'Scanner Ready': 'اسکینر تیار ہے',
                'Product Found': 'پروڈکٹ مل گیا',
                'Product Not Found': 'پروڈکٹ نہیں ملا'
            },
            'ar': {
                'New Sale': 'بيع جديد',
                'Edit Sale': 'تعديل البيع',
                'Sale Details': 'تفاصيل البيع',
                'Sell Products': 'بيع المنتجات',
                'Bill Number': 'رقم الفاتورة',
                'Date': 'التاريخ',
                'Customer Details': 'تفاصيل العميل',
                'Customer Name': 'اسم العميل',
                'Phone Number': 'رقم الهاتف',
                'Address': 'العنوان',
                'Search Products': 'البحث عن منتجات',
                'Product Name': 'المنتج',
                'Product Code': 'رمز المنتج',
                'Batch': 'الدفعة',
                'Price': 'السعر',
                'Quantity': 'الكمية',
                'Total': 'المجموع',
                'Remove': 'إزالة',
                'Stock': 'المخزون',
                'Subtotal': 'المجموع الفرعي',
                'Tax': 'الضريبة',
                'Discount': 'الخصم',
                'Grand Total': 'المجموع الكلي',
                'Paid Amount': 'المبلغ المدفوع',
                'Balance': 'الرصيد',
                'Payment Method': 'طريقة الدفع',
                'Cash': 'نقدي',
                'Card': 'بطاقة',
                'UPI': 'يو بي آي',
                'Complete Sale': 'إتمام البيع',
                'Update Sale': 'تحديث البيع',
                'Clear': 'مسح',
                'Save Draft': 'حفظ المسودة',
                'Drafts': 'المسودات',
                'Saved Drafts': 'المسودات المحفوظة',
                'No drafts saved': 'لا توجد مسودات محفوظة',
                'Draft loaded': 'تم تحميل المسودة',
                'Draft cleared': 'تم مسح المسودة',
                'Clear Form': 'مسح النموذج',
                'View Drafts': 'عرض المسودات',
                'Success': 'نجاح',
                'Error': 'خطأ',
                'Warning': 'تحذير',
                'receipt': 'إيصال',
                'printReceipt': 'طباعة الإيصال',
                'Scanner Ready': 'الماسح جاهز',
                'Product Found': 'تم العثور على المنتج',
                'Product Not Found': 'المنتج غير موجود'
            }
        };

        if (dict[targetLang] && dict[targetLang][text]) {
            return dict[targetLang][text];
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        newSale: 'New Sale',
        editSale: 'Edit Sale',
        saleDetails: 'Sale Details',
        sellProducts: 'Sell Products',
        billNumber: 'Bill Number',
        date: 'Date',
        customerDetails: 'Customer Details',
        customerName: 'Customer Name',
        customerPhone: 'Phone Number',
        customerAddress: 'Address',
        searchProducts: 'Search Products',
        productName: 'Product',
        productCode: 'Product Code',
        batch: 'Batch',
        price: 'Price',
        quantity: 'Qty',
        total: 'Total',
        remove: 'Remove',
        stock: 'Stock',
        subtotal: 'Subtotal',
        tax: 'Tax',
        discount: 'Discount',
        grandTotal: 'Grand Total',
        paidAmount: 'Paid Amount',
        balance: 'Balance',
        paymentMethod: 'Payment Method',
        cash: 'Cash',
        card: 'Card',
        upi: 'UPI',
        completeSale: 'Save & Close',
        updateSale: 'Update Sale',
        clear: 'Clear',
        saving: 'Saving...',
        loading: 'Loading...',
        saleCompleted: 'Sale completed successfully!',
        saleUpdated: 'Sale updated successfully!',
        failedSave: 'Failed to save sale',
        failedUpdate: 'Failed to update sale',
        networkError: 'Network error occurred',
        noShopSelected: 'No shop selected',
        itemsRequired: 'At least one item is required',
        quantityExceedsStock: 'Quantity exceeds available stock',
        invalidQuantity: 'Please enter a valid quantity',
        invalidPrice: 'Please enter a valid price',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        saveDraft: 'Save Draft',
        drafts: 'Drafts',
        savedDrafts: 'Saved Drafts',
        noDraftsSaved: 'No drafts saved',
        draftLoaded: 'Draft loaded',
        draftCleared: 'Draft cleared',
        clearForm: 'Clear Form',
        viewDrafts: 'View Drafts',
        goBack: 'Go Back',
        sellByPack: 'Sell by Pack',
        sellByUnit: 'Sell by Unit',
        receipt: 'Receipt',
        printReceipt: 'Print Receipt',
        thankYou: 'Thank you for your purchase!',
        poweredBy: 'Powered by OurZest',
        scannerReady: 'Scanner Ready',
        productFound: 'Product found!',
        productNotFound: 'Product not found'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const sellTranslator = createSellTranslator();

// ==============================================
// Toast Notification Component
// ==============================================
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations?.success || 'Success';
            case 'error': return translations?.error || 'Error';
            case 'warning': return translations?.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div className={`ozui_toast-notification ${toast.type}`}>
            <div className="ozui_toast-content">
                <div className="ozui_toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozui_toast-text">
                    <div className="ozui_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozui_toast-message">{toast.message}</div>
                </div>
                <button className="ozui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ==============================================
// IOS Style Toggle Switch Component
// ==============================================
const IOSToggleSwitch = ({ checked, onChange, disabled, label, activeLabel, inactiveLabel }) => {
    return (
        <button
            type="button"
            className={`ozui-ios-toggle ${checked ? 'ozui-checked' : ''}`}
            onClick={onChange}
            disabled={disabled}
            role="switch"
            aria-checked={checked}
        >
            <span className="ozui-ios-slider" />
            <span className="ozui-ios-label">
                {checked ? (activeLabel || label || 'Sell by Pack') : (inactiveLabel || label || 'Sell by Unit')}
            </span>
        </button>
    );
};

// ==============================================
// Helper function to calculate item total
// ==============================================
const calculateItemTotal = (price, quantity) => {
    const p = parseFloat(price) || 0;
    const q = parseFloat(quantity) || 0;
    return (p * q).toFixed(2);
};

// ==============================================
// Items Table Component
// ==============================================
const ItemsTable = ({
    items,
    onItemChange,
    onRemove,
    onAdd,
    onToggleSellMethod,
    errors,
    disabled,
    translations,
    isRTL
}) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    if (isMobile) {
        return (
            <div className="ozui-items-mobile-container">
                <div className="ozui-items-header-mobile">
                    <h4><FontAwesomeIcon icon={faBoxes} />{translations?.itemsSold || 'Items'}</h4>
                    <button type="button" className="ozui-btn-add-item-mobile" onClick={onAdd} disabled={disabled}>
                        <FontAwesomeIcon icon={faPlus} /><span>{translations?.addItem || 'Add Item'}</span>
                    </button>
                </div>
                <div className="ozui-items-cards-container">
                    {items.map((item, index) => {
                        const isPackingMode = item.sellMethod === 'packing';
                        return (
                            <div key={index} className={`ozui-item-card-mobile ${isPackingMode ? 'ozui-packing-card' : 'ozui-subpacking-card'}`}>
                                <div className="ozui-item-card-header">
                                    <span className="ozui-item-number">Item {index + 1}</span>
                                    <div className="ozui-sell-method-toggle-mobile">
                                        <span className={`ozui-method-badge ${isPackingMode ? 'ozui-packing' : 'ozui-subpacking'}`}>
                                            {isPackingMode ? (item.packing_type || 'Pack') : (item.sub_packing_type || 'Unit')}
                                        </span>
                                        <button
                                            type="button"
                                            className="ozui-toggle-method-btn"
                                            onClick={() => onToggleSellMethod(index)}
                                            disabled={disabled}
                                        >
                                            <FontAwesomeIcon icon={isPackingMode ? faToggleOn : faToggleOff} />
                                        </button>
                                    </div>
                                    <button type="button" className="ozui-item-remove-btn-mobile" onClick={() => onRemove(index)} disabled={disabled || items.length === 1}>
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                </div>
                                <div className="ozui-item-card-content">
                                    <div className="ozui-mobile-field">
                                        <label>{translations?.productName || 'Product'}</label>
                                        <input type="text" value={item.product_name} readOnly className="ozui-readonly-input" />
                                    </div>
                                    <div className="ozui-mobile-field">
                                        <label>{translations?.productCode || 'Product Code'}</label>
                                        <input type="text" value={item.product_code || ''} readOnly className="ozui-readonly-input" />
                                    </div>
                                    <div className="ozui-mobile-field">
                                        <label>{translations?.batchNumber || 'Batch'}</label>
                                        <input type="text" value={item.batch_no} readOnly className="ozui-readonly-input" />
                                    </div>
                                    <div className="ozui-mobile-row">
                                        <div className={`ozui-mobile-field half ${errors[`price_${index}`] ? 'ozui-has-error' : ''}`}>
                                            <label>{translations?.price || 'Price'} *</label>
                                            <input type="number" value={item.price} onChange={(e) => onItemChange(index, 'price', parseFloat(e.target.value))} step="0.01" min="0" placeholder="0.00" disabled={disabled} dir="ltr" />
                                        </div>
                                    </div>
                                    <div className="ozui-mobile-row">
                                        <div className={`ozui-mobile-field half ${errors[`quantity_${index}`] ? 'ozui-has-error' : ''}`}>
                                            <label>{translations?.quantity || 'Qty'} *</label>
                                            <input type="number" value={item.quantity} onChange={(e) => onItemChange(index, 'quantity', parseFloat(e.target.value))} step="0.001" min="0.001" max={item.available_stock} placeholder="0.000" disabled={disabled} dir="ltr" />
                                            <small className="ozui-stock-hint">
                                                {translations?.availableStock || 'Available'}: {parseFloat(item.available_stock || 0).toFixed(2)} {item.display_unit}
                                            </small>
                                        </div>
                                        <div className="ozui-mobile-field half">
                                            <label>{translations?.total || 'Total'}</label>
                                            <input type="text" value={calculateItemTotal(item.price, item.quantity)} readOnly disabled className="ozui-readonly-input ozui-total-display" />
                                        </div>
                                    </div>
                                    {errors[`quantity_${index}`] && <div className="ozui-error-text"><FontAwesomeIcon icon={faExclamationTriangle} />{errors[`quantity_${index}`]}</div>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    // DESKTOP VIEW
    return (
        <div className="ozui-items-table-container">
            <div className="ozui-table-header-actions">
                <h4><FontAwesomeIcon icon={faBoxes} />{translations?.itemsSold || 'Items'}</h4>
                <button type="button" className="ozui-btn-add-item" onClick={onAdd} disabled={disabled}>
                    <FontAwesomeIcon icon={faPlus} /><span>{translations?.addItem || 'Add Item'}</span>
                </button>
            </div>
            <div className="ozui-table-wrapper">
                <table className="ozui-items-table">
                    <thead>
                        <tr>
                            <th>{translations?.productName || 'Product'} <span className="ozui-required">*</span></th>
                            <th>{translations?.productCode || 'Product Code'}</th>
                            <th>{translations?.batchNumber || 'Batch'}</th>
                            <th>{translations?.sellUnit || 'Sell Method'}</th>
                            <th>{translations?.price || 'Price'} <span className="ozui-required">*</span></th>
                            <th>{translations?.quantity || 'Qty'} <span className="ozui-required">*</span></th>
                            <th>{translations?.stock || 'Stock'}</th>
                            <th>{translations?.total || 'Total'}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => {
                            const isPackingMode = item.sellMethod === 'packing';
                            const sellByPackLabel = `Sell by ${item.packing_type || 'Pack'}`;
                            const sellByUnitLabel = `Sell by ${item.sub_packing_type || item.unit_of_measure || 'Unit'}`;

                            const availableStockDisplay = isPackingMode
                                ? `${parseFloat(item.available_stock_packs || 0).toFixed(2)} ${item.packing_type || 'packs'}`
                                : `${parseFloat(item.available_stock_units || 0).toFixed(2)} ${item.sub_packing_type || item.unit_of_measure || 'units'}`;

                            return (
                                <tr key={index} className={`${errors[`quantity_${index}`] ? 'ozui-error-row' : ''}`}>
                                    <td>
                                        <div className="ozui-table-field"><input type="text" value={item.product_name} readOnly className="ozui-readonly-input" /></div>
                                    </td>
                                    <td>
                                        <div className="ozui-table-field"><input type="text" value={item.product_code || ''} readOnly className="ozui-readonly-input" /></div>
                                    </td>
                                    <td>
                                        <div className="ozui-table-field"><input type="text" value={item.batch_no} readOnly className="ozui-readonly-input" /></div>
                                    </td>
                                    <td>
                                        <div className="ozui-table-field">
                                            <IOSToggleSwitch
                                                checked={isPackingMode}
                                                onChange={() => onToggleSellMethod(index)}
                                                disabled={disabled}
                                                activeLabel={sellByPackLabel}
                                                inactiveLabel={sellByUnitLabel}
                                            />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-table-field">
                                            <input type="number" value={item.price} onChange={(e) => onItemChange(index, 'price', parseFloat(e.target.value))} step="0.01" min="0" placeholder="0.00" disabled={disabled} dir="ltr" className={errors[`price_${index}`] ? 'ozui-error' : ''} />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-table-field">
                                            <input type="number" value={item.quantity} onChange={(e) => onItemChange(index, 'quantity', parseFloat(e.target.value))} step="0.001" min="0.001" max={item.available_stock} placeholder="0.000" disabled={disabled} dir="ltr" className={errors[`quantity_${index}`] ? 'ozui-error' : ''} />
                                            {errors[`quantity_${index}`] && <div className="ozui-table-error"><FontAwesomeIcon icon={faExclamationTriangle} />{errors[`quantity_${index}`]}</div>}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-table-field">
                                            <span className={`ozui-stock-badge ${parseFloat(item.available_stock || 0) <= (item.quantity_alert || 5) ? 'ozui-low-stock' : ''}`}>
                                                {availableStockDisplay}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-table-field"><input type="text" value={calculateItemTotal(item.price, item.quantity)} readOnly disabled className="ozui-readonly-input ozui-total-display" /></div>
                                    </td>
                                    <td>
                                        <button type="button" className="ozui-table-remove-btn" onClick={() => onRemove(index)} disabled={disabled || items.length === 1} title={translations?.remove || 'Remove'}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ==============================================
// Main Sell Component
// ==============================================
const Sell = ({ saleId = null, onSuccess = null }) => {
    const { user, currentShop } = useAuth();

    const initialFormData = {
        date: new Date().toISOString().split('T')[0],
        bill_number: '',
        bill_number_int: 0,
        customer_name: '',
        customer_phone: '',
        customer_address: '',
        customer_id: null,
        subtotal: '0.00',
        tax: '0.00',
        discount: '0.00',
        grand_total: '0.00',
        paid_amount: '0.00',
        balance: '0.00',
        payment_method: 'cash',
        payment_status: 'unpaid',
        notes: '',
        items: [],
        packing_status: 'unpacked'
    };

    const [formData, setFormData] = useState(initialFormData);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [errors, setErrors] = useState({});
    const [currentLanguage, setCurrentLanguage] = useState(() => localStorage.getItem('ourzest_language') || 'en');
    const [translations, setTranslations] = useState({});
    const [darkMode, setDarkMode] = useState(() => localStorage.getItem('ourzest_darkMode') === 'true');
    const [currentThemeColor, setCurrentThemeColor] = useState(() => localStorage.getItem('ourzest_theme') || '#6366f1');
    const [toast, setToast] = useState({ show: false, type: '', message: '' });

    // Draft State
    const [showDraftsList, setShowDraftsList] = useState(false);
    const [savedDrafts, setSavedDrafts] = useState([]);

    // Customer & Product Search
    const [customerSearchResults, setCustomerSearchResults] = useState([]);
    const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
    const [searchingCustomer, setSearchingCustomer] = useState(false);

    const [productSearchTerm, setProductSearchTerm] = useState('');
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [showProductDropdown, setShowProductDropdown] = useState(false);
    const [searchingProducts, setSearchingProducts] = useState(false);

    // Barcode Scanner State
    const [isScannerActive, setIsScannerActive] = useState(true);
    const [lastScannedBarcode, setLastScannedBarcode] = useState('');
    const barcodeInputRef = useRef(null);
    const scanCooldownRef = useRef(false);
    const scanTimerRef = useRef(null);

    // Business Info for Receipt
    const [businessInfo, setBusinessInfo] = useState({
        businessname: '',
        businessaddress: '',
        phonenumber: '',
        email: '',
        bussinesslogo: '',
        bussinesslogo_url: ''
    });

    // Auto-focus ref for Customer Name field
    const customerNameRef = useRef(null);

    // Refs
    const toastTimerRef = useRef(null);
    const customerSearchRef = useRef(null);
    const productSearchRef = useRef(null);
    const isSavingRef = useRef(false);
    const submitButtonRef = useRef(null);
    const draftsDropdownRef = useRef(null);

    // ==============================================
    // GLOBAL BARCODE SCANNER
    // ==============================================
    useEffect(() => {
        let buffer = '';
        let lastTime = 0;

        const handler = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
                if (e.target === barcodeInputRef.current) return;
                return;
            }
            const now = Date.now();
            if (now - lastTime > 50) buffer = '';
            lastTime = now;
            if (e.key.length === 1) buffer += e.key;
            if (e.key === 'Enter' && buffer.length >= 6) {
                e.preventDefault();
                e.stopPropagation();
                const code = buffer.trim();
                buffer = '';
                if (code && isScannerActive) processBarcodeScan(code);
            }
            clearTimeout(scanTimerRef.current);
            scanTimerRef.current = setTimeout(() => { buffer = ''; }, 100);
        };

        document.addEventListener('keypress', handler, true);
        return () => {
            document.removeEventListener('keypress', handler, true);
            clearTimeout(scanTimerRef.current);
        };
    }, [isScannerActive, formData.items]);

    // ==============================================
    // Process Barcode Scan - properly processes product like searchProducts
    // ==============================================
    const processBarcodeScan = useCallback(async (barcode) => {
        if (scanCooldownRef.current) return;
        scanCooldownRef.current = true;
        setLastScannedBarcode(barcode);

        if (!user?.username || !currentShop?.shop_id) {
            showToast('error', 'No shop selected');
            scanCooldownRef.current = false;
            return;
        }

        try {
            const response = await fetch(PRODUCT_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'search_products',
                    searchTerm: barcode,
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });
            const data = await response.json();

            if (data.status === 'success' && data.data && data.data.length > 0) {
                // Find exact barcode match first, then product_code match
                const exactMatch = data.data.find(
                    p => p.barcode === barcode || p.product_code === barcode
                );
                const rawProduct = exactMatch || data.data[0];

                // Process the product the SAME way searchProducts does
                let hasPackaging = false;
                let packingType = null;
                let subPackingType = null;
                let itemsPerPack = 1;
                let unitOfMeasure = rawProduct.unit_of_measure || 'piece';
                let sellPerPack = null;

                if (rawProduct.packaging_info) {
                    try {
                        const info = typeof rawProduct.packaging_info === 'string'
                            ? JSON.parse(rawProduct.packaging_info)
                            : rawProduct.packaging_info;
                        if (info) {
                            hasPackaging = true;
                            packingType = info.packing_type;
                            subPackingType = info.sub_packing_type;
                            itemsPerPack = parseFloat(info.items_per_pack) || 1;
                            unitOfMeasure = info.unit_of_measure || rawProduct.unit_of_measure || 'piece';
                            sellPerPack = info.sell_per_pack ? parseFloat(info.sell_per_pack) : null;
                        }
                    } catch (e) {}
                }

                if (rawProduct.sell_per_pack && parseFloat(rawProduct.sell_per_pack) > 0) {
                    sellPerPack = parseFloat(rawProduct.sell_per_pack);
                }
                if (rawProduct.packing_type) packingType = rawProduct.packing_type;
                if (rawProduct.sub_packing_type) subPackingType = rawProduct.sub_packing_type;
                if (rawProduct.items_per_pack && parseFloat(rawProduct.items_per_pack) > 0) {
                    itemsPerPack = parseFloat(rawProduct.items_per_pack);
                }

                const packStock = parseFloat(rawProduct.quantity) || 0;
                const unitStock = parseBQuantityNumeric(rawProduct.bquantity || '0');

                const sellPricePerUnit = parseFloat(rawProduct.sell_price_per_unit) > 0
                    ? parseFloat(rawProduct.sell_price_per_unit)
                    : parseFloat(rawProduct.sell_price) || 0;

                // Build the properly processed product object
                const processedProduct = {
                    id: rawProduct.id,
                    product_name: rawProduct.product_name,
                    product_code: rawProduct.product_code || '',
                    batch_no: rawProduct.batch_no || '',
                    barcode: rawProduct.barcode || '',
                    sell_price: parseFloat(rawProduct.sell_price) || 0,
                    sell_per_pack: sellPerPack,
                    sell_price_per_unit: sellPricePerUnit,
                    quantity: packStock,
                    bquantity: rawProduct.bquantity || '',
                    available_bulk: unitStock,
                    available_pack_stock: packStock,
                    available_unit_stock: unitStock,
                    quantity_alert: parseFloat(rawProduct.quantity_alert) || 5,
                    category: rawProduct.category || '',
                    company: rawProduct.company || '',
                    has_packaging: hasPackaging,
                    packing_type: packingType,
                    sub_packing_type: subPackingType,
                    items_per_pack: itemsPerPack,
                    unit_of_measure: unitOfMeasure
                };

                // Call handleProductSelect with the properly processed product
                handleProductSelect(processedProduct);
                showToast('success', `${rawProduct.product_name} - ${translations?.productFound || 'Product found!'}`);
            } else {
                showToast('error', `${translations?.productNotFound || 'Product not found'}: ${barcode}`);
            }
        } catch (error) {
            console.error('Barcode search error:', error);
            showToast('error', 'Error searching product');
        }

        setTimeout(() => { scanCooldownRef.current = false; }, 500);
    }, [user?.username, currentShop?.shop_id, translations]);

    // Tab key handler to close all dropdowns
    useEffect(() => {
        const handleTabKey = (e) => {
            if (e.key === 'Tab') {
                setShowCustomerDropdown(false);
                setShowProductDropdown(false);
                setShowDraftsList(false);
            }
        };

        document.addEventListener('keydown', handleTabKey);
        return () => document.removeEventListener('keydown', handleTabKey);
    }, []);

    // Auto-focus on Customer Name field when component mounts
    useEffect(() => {
        setTimeout(() => {
            if (customerNameRef.current) {
                customerNameRef.current.focus();
            }
        }, 100);
    }, []);

    // ============================================
    // Draft Management Functions
    // ============================================
    const getDraftStorageKey = () => {
        const userId = user?.user_id || user?.username || 'anonymous';
        const shopId = currentShop?.shop_id || 'no_shop';
        return `sell_draft_${userId}_${shopId}`;
    };

    const saveDraft = () => {
        const hasData = formData.items.length > 0 || formData.customer_name?.trim() || formData.customer_phone?.trim();
        if (!hasData) {
            showToast('warning', 'Cannot save empty draft');
            return;
        }

        const draftId = Date.now();
        const draft = {
            id: draftId,
            ...formData,
            savedAt: new Date().toISOString(),
            shop_id: currentShop?.shop_id
        };

        const existingDrafts = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
        existingDrafts.unshift(draft);
        const limitedDrafts = existingDrafts.slice(0, 20);
        localStorage.setItem(getDraftStorageKey(), JSON.stringify(limitedDrafts));

        showToast('success', 'Draft saved successfully!');
        loadDrafts();
    };

    const loadDrafts = () => {
        const drafts = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
        const filteredDrafts = currentShop ? drafts.filter(d => d.shop_id === currentShop?.shop_id) : drafts;
        setSavedDrafts(filteredDrafts);
    };

    const loadDraft = (draft) => {
        setFormData({
            ...initialFormData,
            ...draft,
            date: draft.date || new Date().toISOString().split('T')[0],
            items: draft.items || []
        });
        setShowDraftsList(false);
        showToast('success', translations.draftLoaded || 'Draft loaded successfully!');

        setTimeout(() => {
            if (customerNameRef.current) {
                customerNameRef.current.focus();
            }
        }, 100);
    };

    const deleteDraft = (draftId) => {
        const drafts = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
        const filteredDrafts = drafts.filter(d => d.id !== draftId);
        localStorage.setItem(getDraftStorageKey(), JSON.stringify(filteredDrafts));
        loadDrafts();
        showToast('success', 'Draft deleted successfully!');
    };

    const clearDraft = () => {
        setFormData(initialFormData);
        setProductSearchTerm('');
        setErrors({});
        setLastScannedBarcode('');
        showToast('success', translations.draftCleared || 'Form cleared');

        setTimeout(() => {
            if (customerNameRef.current) {
                customerNameRef.current.focus();
            }
        }, 100);
    };

    // ============================================
    // Theme Color Auto-Sync
    // ============================================
    const applyTheme = useCallback((color) => {
        if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
            color = '#6366f1';
        }
        document.documentElement.style.setProperty('--ozui-primary', color);
        document.documentElement.style.setProperty('--ozui-primary-dark', color);
        document.documentElement.style.setProperty('--ozui-primary-light', color);

        let r, g, b;
        if (color.startsWith('#')) {
            r = parseInt(color.slice(1, 3), 16);
            g = parseInt(color.slice(3, 5), 16);
            b = parseInt(color.slice(5, 7), 16);
        } else {
            r = 99;
            g = 102;
            b = 241;
        }

        let style = document.getElementById('dynamic-orb-styles-sell');
        if (!style) {
            style = document.createElement('style');
            style.id = 'dynamic-orb-styles-sell';
            document.head.appendChild(style);
        }

        style.textContent = `
            .ozui-header-bg-animation .gradient-orb.orb-1 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.4) 0%, rgba(${r}, ${g}, ${b}, 0.1) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-2 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.35) 0%, rgba(${r}, ${g}, ${b}, 0.08) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-3 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.3) 0%, rgba(${r}, ${g}, ${b}, 0.05) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-4 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.25) 0%, rgba(${r}, ${g}, ${b}, 0.03) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-5 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.2) 0%, rgba(${r}, ${g}, ${b}, 0.02) 100%);
            }
        `;

        setCurrentThemeColor(color);
    }, []);

    useEffect(() => {
        const savedTheme = localStorage.getItem('ourzest_theme') || '#6366f1';
        applyTheme(savedTheme);

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') {
                applyTheme(e.newValue || '#6366f1');
            }
        };

        const handleThemeChanged = (event) => {
            if (event.detail && event.detail.theme) {
                applyTheme(event.detail.theme);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
        };
    }, [applyTheme]);

    // ============================================
    // Dark Mode Auto-Sync
    // ============================================
    useEffect(() => {
        const syncDarkMode = () => {
            const isDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(isDarkMode);

            const container = document.querySelector('.ozui-container');
            if (container) {
                if (isDarkMode) {
                    container.classList.add('ozui-dark');
                } else {
                    container.classList.remove('ozui-dark');
                }
            }

            if (isDarkMode) {
                document.body.classList.add('ozui-dark');
                document.documentElement.classList.add('ozui-dark');
            } else {
                document.body.classList.remove('ozui-dark');
                document.documentElement.classList.remove('ozui-dark');
            }
        };

        syncDarkMode();

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_darkMode') {
                syncDarkMode();
            }
        };

        const handleDarkModeToggle = (event) => {
            if (event.detail) {
                const isDark = event.detail.darkMode;
                setDarkMode(isDark);
                const container = document.querySelector('.ozui-container');
                if (container) {
                    if (isDark) {
                        container.classList.add('ozui-dark');
                    } else {
                        container.classList.remove('ozui-dark');
                    }
                }
            } else {
                syncDarkMode();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('darkModeToggled', handleDarkModeToggle);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('darkModeToggled', handleDarkModeToggle);
        };
    }, []);

    // ============================================
    // Language Auto-Sync
    // ============================================
    useEffect(() => {
        const loadTranslations = async (lang) => {
            const trans = await sellTranslator.getTranslations(lang);
            setTranslations(trans);
        };

        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };

        const handleCustomLanguageChange = (event) => {
            if (event.detail && event.detail.language) {
                const newLang = event.detail.language;
                if (newLang !== currentLanguage) {
                    setCurrentLanguage(newLang);
                    loadTranslations(newLang);
                }
            }
        };

        loadTranslations(currentLanguage);

        window.addEventListener('storage', handleLanguageChange);
        window.addEventListener('languageChanged', handleCustomLanguageChange);

        const interval = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            window.removeEventListener('languageChanged', handleCustomLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    // ============================================
    // RTL Direction Sync
    // ============================================
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const container = document.querySelector('.ozui-container');

        if (container) {
            if (isRTL) {
                container.classList.add('ozui-rtl');
                container.classList.remove('ozui-ltr');
            } else {
                container.classList.add('ozui-ltr');
                container.classList.remove('ozui-rtl');
            }
        }

        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
        }
    }, [currentLanguage]);

    // Load drafts on shop change
    useEffect(() => {
        loadDrafts();
    }, [currentShop]);

    // Position drafts dropdown
    useEffect(() => {
        if (showDraftsList && draftsDropdownRef.current) {
            const button = draftsDropdownRef.current.querySelector('.drafts-btn');
            const dropdown = draftsDropdownRef.current.querySelector('.ozui-drafts-dropdown');

            if (button && dropdown) {
                const rect = button.getBoundingClientRect();
                dropdown.style.position = 'fixed';
                dropdown.style.top = `${rect.bottom + 8}px`;
                dropdown.style.right = `${window.innerWidth - rect.right}px`;
            }
        }
    }, [showDraftsList]);

    // Click outside for drafts dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (draftsDropdownRef.current && !draftsDropdownRef.current.contains(event.target)) {
                setShowDraftsList(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Click outside for customer/product dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (customerSearchRef.current && !customerSearchRef.current.contains(event.target)) {
                setShowCustomerDropdown(false);
            }
            if (productSearchRef.current && !productSearchRef.current.contains(event.target)) {
                setShowProductDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => {
            setToast({ show: false, type: '', message: '' });
        }, 3000);
    }, []);

    useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        };
    }, []);

    const getNextBillNumber = async () => {
        if (!currentShop?.shop_id) return;
        try {
            const response = await fetch(SELL_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_next_bill_number', shop_id: currentShop.shop_id })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setFormData(prev => ({ ...prev, bill_number: data.data.bill_number, bill_number_int: data.data.bill_number_int }));
            }
        } catch (error) {
            console.error('Error getting next bill number:', error);
        }
    };

    // ============================================
    // Fetch Business Info for Receipt
    // ============================================
    const fetchBusinessInfo = useCallback(async () => {
        if (!user || !user.username) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/root_users.php?action=get_business_info&username=' + encodeURIComponent(user.username), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include'
            });
            const data = await response.json();
            if (data.status === 'success') {
                setBusinessInfo({
                    businessname: data.businessname || '',
                    businessaddress: data.businessaddress || '',
                    phonenumber: data.phonenumber || '',
                    email: data.email || '',
                    bussinesslogo: data.bussinesslogo || '',
                    bussinesslogo_url: data.bussinesslogo_url || ''
                });
            } else {
                setBusinessInfo({
                    businessname: user.business_name || user.fullname || '',
                    businessaddress: user.business_address || '',
                    phonenumber: user.phonenumber || '',
                    email: user.email || '',
                    bussinesslogo: '',
                    bussinesslogo_url: ''
                });
            }
        } catch (error) {
            console.error('Error fetching business info:', error);
            setBusinessInfo({
                businessname: user.business_name || user.fullname || '',
                businessaddress: user.business_address || '',
                phonenumber: user.phonenumber || '',
                email: user.email || '',
                bussinesslogo: '',
                bussinesslogo_url: ''
            });
        }
    }, [user]);

    useEffect(() => {
        if (currentShop?.shop_id) {
            getNextBillNumber();
        }
        fetchBusinessInfo();
    }, [currentShop, fetchBusinessInfo]);

    useEffect(() => {
        if (saleId) {
            setIsEditMode(true);
            loadSaleData(saleId);
        }
    }, [saleId]);

    useEffect(() => {
        calculateTotals();
    }, [formData.items, formData.tax, formData.discount, formData.paid_amount]);

    const loadSaleData = async (id) => {
        if (!user?.username || !currentShop?.shop_id) return;
        setLoading(true);
        try {
            const response = await fetch(SELL_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_record', id: id, username: user.username, shop_id: currentShop.shop_id })
            });
            const data = await response.json();
            if (data.status === 'success' && data.data) {
                const sale = data.data;
                setFormData(prev => ({
                    ...prev,
                    date: sale.date || new Date().toISOString().split('T')[0],
                    bill_number: sale.bill_number || '',
                    bill_number_int: sale.bill_number_int || 0,
                    customer_id: sale.customer_id || null,
                    customer_name: sale.customer_name || '',
                    customer_phone: sale.customer_phone || '',
                    customer_address: sale.customer_address || '',
                    subtotal: sale.subtotal || '0.00',
                    tax: sale.tax || '0.00',
                    discount: sale.discount || '0.00',
                    grand_total: sale.grand_total || '0.00',
                    paid_amount: sale.paid_amount || '0.00',
                    balance: sale.balance || '0.00',
                    payment_method: sale.payment_method || 'cash',
                    payment_status: sale.payment_status || 'unpaid',
                    notes: sale.notes || '',
                    items: sale.items || [],
                    packing_status: sale.packing_status || 'unpacked'
                }));
            } else {
                showToast('error', data.message || 'Failed to load sale data');
            }
        } catch (error) {
            showToast('error', translations?.networkError || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    const searchCustomers = useCallback(async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 2 || !currentShop?.shop_id) {
            setCustomerSearchResults([]);
            setShowCustomerDropdown(false);
            return;
        }
        setSearchingCustomer(true);
        try {
            const response = await fetch(CUSTOMER_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_records', shop_id: currentShop.shop_id })
            });
            const data = await response.json();
            if (data.status === 'success' && data.data) {
                const filtered = data.data.filter(customer =>
                    customer.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    customer.phone?.toLowerCase().includes(searchTerm.toLowerCase())
                );
                setCustomerSearchResults(filtered.slice(0, 20));
                setShowCustomerDropdown(filtered.length > 0);
            } else {
                setCustomerSearchResults([]);
                setShowCustomerDropdown(false);
            }
        } catch (error) {
            console.error('Error searching customers:', error);
            setCustomerSearchResults([]);
            setShowCustomerDropdown(false);
        } finally {
            setSearchingCustomer(false);
        }
    }, [currentShop?.shop_id]);

    // ============================================
    // searchProducts - correctly map sell_price_per_unit and sell_per_pack
    // ============================================
    const searchProducts = useCallback(async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 2 || !user?.username || !currentShop?.shop_id) {
            setFilteredProducts([]);
            setShowProductDropdown(false);
            return;
        }
        setSearchingProducts(true);
        try {
            const response = await fetch(PRODUCT_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'search_products',
                    searchTerm: searchTerm,
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                const products = (data.data || []).map(product => {
                    let hasPackaging = false;
                    let packingType = null;
                    let subPackingType = null;
                    let itemsPerPack = 1;
                    let unitOfMeasure = product.unit_of_measure || 'piece';
                    let sellPerPack = null;

                    if (product.packaging_info) {
                        try {
                            const info = typeof product.packaging_info === 'string' ? JSON.parse(product.packaging_info) : product.packaging_info;
                            if (info) {
                                hasPackaging = true;
                                packingType = info.packing_type;
                                subPackingType = info.sub_packing_type;
                                itemsPerPack = parseFloat(info.items_per_pack) || 1;
                                unitOfMeasure = info.unit_of_measure || product.unit_of_measure || 'piece';
                                sellPerPack = info.sell_per_pack ? parseFloat(info.sell_per_pack) : null;
                            }
                        } catch (e) { }
                    }

                    // Prefer direct DB columns over packaging_info
                    if (product.sell_per_pack && parseFloat(product.sell_per_pack) > 0) {
                        sellPerPack = parseFloat(product.sell_per_pack);
                    }
                    if (product.packing_type) packingType = product.packing_type;
                    if (product.sub_packing_type) subPackingType = product.sub_packing_type;
                    if (product.items_per_pack && parseFloat(product.items_per_pack) > 0) {
                        itemsPerPack = parseFloat(product.items_per_pack);
                    }

                    const packStock = parseFloat(product.quantity) || 0;
                    const unitStock = parseBQuantityNumeric(product.bquantity || '0');

                    const sellPricePerUnit = parseFloat(product.sell_price_per_unit) > 0
                        ? parseFloat(product.sell_price_per_unit)
                        : parseFloat(product.sell_price) || 0;

                    return {
                        id: product.id,
                        product_name: product.product_name,
                        product_code: product.product_code || '',
                        batch_no: product.batch_no || '',
                        barcode: product.barcode || '',
                        sell_price: parseFloat(product.sell_price) || 0,
                        sell_per_pack: sellPerPack,
                        sell_price_per_unit: sellPricePerUnit,
                        quantity: packStock,
                        bquantity: product.bquantity || '',
                        available_bulk: unitStock,
                        available_pack_stock: packStock,
                        available_unit_stock: unitStock,
                        quantity_alert: parseFloat(product.quantity_alert) || 5,
                        category: product.category || '',
                        company: product.company || '',
                        has_packaging: hasPackaging,
                        packing_type: packingType,
                        sub_packing_type: subPackingType,
                        items_per_pack: itemsPerPack,
                        unit_of_measure: unitOfMeasure
                    };
                });
                setFilteredProducts(products);
                setShowProductDropdown(products.length > 0);
            } else {
                setFilteredProducts([]);
                setShowProductDropdown(false);
            }
        } catch (error) {
            console.error('Error searching products:', error);
            setFilteredProducts([]);
            setShowProductDropdown(false);
        } finally {
            setSearchingProducts(false);
        }
    }, [user?.username, currentShop?.shop_id]);

    const selectCustomer = (customer) => {
        setFormData(prev => ({
            ...prev,
            customer_id: customer.customerid,
            customer_name: customer.customer_name,
            customer_phone: customer.phone || '',
            customer_address: customer.address || ''
        }));
        setShowCustomerDropdown(false);
        setCustomerSearchResults([]);
        showToast('success', `${translations?.customerSelected || 'Customer selected'}: ${customer.customer_name}`);

        setTimeout(() => {
            const productSearchInput = productSearchRef.current?.querySelector('input');
            if (productSearchInput) {
                productSearchInput.focus();
            }
        }, 100);
    };

    // ============================================
    // toggleSellMethod - FIXED: Proper quantity conversion
    // ============================================
    const toggleSellMethod = (index) => {
        const updatedItems = [...formData.items];
        const item = updatedItems[index];
        const currentMethod = item.sellMethod;
        const newMethod = currentMethod === 'packing' ? 'sub_packing' : 'packing';
        const currentQty = parseFloat(item.quantity) || 0;

        let newPrice = 0;
        let newMaxStock = 0;
        let newDisplayUnit = '';
        let convertedQty = currentQty;

        if (newMethod === 'packing') {
            // Switching TO pack: show pack price
            newPrice = item.pack_price > 0 ? item.pack_price : (item.unit_price * (item.items_per_pack || 1));
            newMaxStock = item.available_stock_packs || 0;
            newDisplayUnit = item.packing_type || 'pack';
            // Convert units to packs (divide by items_per_pack)
            if (currentMethod === 'sub_packing' && currentQty > 0) {
                convertedQty = currentQty / (item.items_per_pack || 1);
            }
        } else {
            // Switching TO unit: show unit price
            newPrice = item.unit_price > 0 ? item.unit_price : (item.pack_price / (item.items_per_pack || 1));
            newMaxStock = item.available_stock_units || 0;
            newDisplayUnit = item.sub_packing_type || item.unit_of_measure || 'unit';
            // Convert packs to units (multiply by items_per_pack)
            if (currentMethod === 'packing' && currentQty > 0) {
                convertedQty = currentQty * (item.items_per_pack || 1);
            }
        }

        // Round to nearest whole number if it's very close (for units)
        if (newMethod === 'sub_packing' && convertedQty > 0) {
            const rounded = Math.round(convertedQty * 1000) / 1000;
            if (Math.abs(rounded - Math.round(rounded)) < 0.001) {
                convertedQty = Math.round(rounded);
            }
        }

        // For packs, round to 3 decimal places
        if (newMethod === 'packing' && convertedQty > 0) {
            convertedQty = parseFloat(convertedQty.toFixed(3));
        }

        if (convertedQty > newMaxStock && newMaxStock > 0) {
            showToast('warning', `Not enough stock. Quantity adjusted.`);
            convertedQty = newMaxStock;
        }

        // Ensure quantity is at least 1
        if (convertedQty <= 0) {
            convertedQty = 1;
        }

        updatedItems[index] = {
            ...item,
            sellMethod: newMethod,
            price: newPrice,
            available_stock: newMaxStock,
            display_unit: newDisplayUnit,
            quantity: convertedQty.toString(),
            total: ((newPrice || 0) * convertedQty).toFixed(2)
        };

        setFormData(prev => ({ ...prev, items: updatedItems }));

        if (convertedQty > newMaxStock && newMaxStock > 0) {
            setErrors(prev => ({ ...prev, [`quantity_${index}`]: 'Quantity exceeds available stock' }));
        } else {
            setErrors(prev => { const newErrors = { ...prev }; delete newErrors[`quantity_${index}`]; return newErrors; });
        }
    };

    // ============================================
    // handleProductSelect - Fixed price mapping
    // ============================================
    const handleProductSelect = (product) => {
        const existingItemIndex = formData.items.findIndex(item => item.product_id === product.id);

        const availablePacks = product.available_pack_stock || 0;
        const availableUnits = product.available_unit_stock || 0;

        const hasPackaging = product.has_packaging && product.packing_type && product.sub_packing_type;
        const itemsPerPack = product.items_per_pack || 1;

        let unitPrice = product.sell_price_per_unit > 0 ? product.sell_price_per_unit : product.sell_price;
        let packPrice = product.sell_per_pack > 0 ? product.sell_per_pack : (unitPrice * itemsPerPack);

        if (packPrice <= 0 && unitPrice > 0) packPrice = unitPrice * itemsPerPack;
        if (unitPrice <= 0 && packPrice > 0) unitPrice = packPrice / itemsPerPack;

        if (existingItemIndex >= 0) {
            const updatedItems = [...formData.items];
            const existingItem = updatedItems[existingItemIndex];
            const increment = 1;
            const maxQty = existingItem.sellMethod === 'packing' ? availablePacks : availableUnits;
            const newQty = parseFloat(existingItem.quantity) + increment;

            if (newQty <= maxQty) {
                updatedItems[existingItemIndex] = {
                    ...existingItem,
                    quantity: newQty,
                    total: (parseFloat(existingItem.price) * newQty).toFixed(2)
                };
                setFormData(prev => ({ ...prev, items: updatedItems }));
                showToast('success', `Added another ${product.product_name}`);
            } else {
                showToast('error', `Cannot add more than available stock`);
            }
        } else {
            let defaultMethod = 'sub_packing';
            let defaultPrice = unitPrice;
            let defaultStock = availableUnits;
            let defaultUnit = product.sub_packing_type || product.unit_of_measure || 'unit';

            if (availableUnits <= 0 && availablePacks > 0) {
                defaultMethod = 'packing';
                defaultPrice = packPrice;
                defaultStock = availablePacks;
                defaultUnit = product.packing_type || 'pack';
            }

            const newItem = {
                product_id: product.id,
                product_name: product.product_name,
                product_code: product.product_code || '',
                batch_no: product.batch_no || '',
                barcode: product.barcode || '',
                category: product.category || '',
                company: product.company || '',
                sellMethod: defaultMethod,
                display_unit: defaultUnit,
                packing_type: product.packing_type,
                sub_packing_type: product.sub_packing_type,
                items_per_pack: itemsPerPack,
                unit_of_measure: product.unit_of_measure || 'piece',
                has_packaging: hasPackaging,
                available_stock_packs: availablePacks,
                available_stock_units: availableUnits,
                available_stock: defaultStock,
                price: defaultPrice,
                pack_price: packPrice,
                unit_price: unitPrice,
                quantity: '1',
                total: defaultPrice,
                quantity_alert: product.quantity_alert || 5
            };

            setFormData(prev => ({ ...prev, items: [...prev.items, newItem] }));
            showToast('success', `${product.product_name} added`);
        }

        setProductSearchTerm('');
        setShowProductDropdown(false);
    };

    const handleItemChange = (index, field, value) => {
        const updatedItems = [...formData.items];
        const item = updatedItems[index];

        if (field === 'price') {
            item.price = value;
            item.total = (value * (parseFloat(item.quantity) || 0)).toFixed(2);
        } else if (field === 'quantity') {
            const qty = value;
            const maxQty = item.available_stock || 0;
            if (qty > maxQty) {
                setErrors(prev => ({ ...prev, [`quantity_${index}`]: translations?.quantityExceedsStock || 'Quantity exceeds available stock' }));
            } else {
                setErrors(prev => { const newErrors = { ...prev }; delete newErrors[`quantity_${index}`]; return newErrors; });
            }
            item.quantity = qty;
            item.total = ((parseFloat(item.price) || 0) * qty).toFixed(2);
        }

        updatedItems[index] = item;
        setFormData(prev => ({ ...prev, items: updatedItems }));
    };

    const addItem = () => {
        if (productSearchRef.current) {
            const input = productSearchRef.current.querySelector('input');
            if (input) input.focus();
        }
    };

    const removeItem = (index) => {
        if (formData.items.length > 1) {
            const updatedItems = [...formData.items];
            updatedItems.splice(index, 1);
            setFormData(prev => ({ ...prev, items: updatedItems }));
        }
    };

    const calculateTotals = () => {
        let subtotal = 0;
        formData.items.forEach(item => {
            const price = parseFloat(item.price) || 0;
            const quantity = parseFloat(item.quantity) || 0;
            subtotal += price * quantity;
        });
        const tax = parseFloat(formData.tax) || 0;
        const discount = parseFloat(formData.discount) || 0;
        const grandTotal = subtotal + tax - discount;
        const paid = parseFloat(formData.paid_amount) || 0;
        const balance = grandTotal - paid;
        const paymentStatus = balance <= 0 ? 'paid' : (paid > 0 ? 'partial' : 'unpaid');
        setFormData(prev => ({
            ...prev,
            subtotal: subtotal.toFixed(2),
            grand_total: grandTotal.toFixed(2),
            balance: balance.toFixed(2),
            payment_status: paymentStatus
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.items || formData.items.length === 0) {
            newErrors.items = translations?.itemsRequired || 'At least one item is required';
        } else {
            formData.items.forEach((item, index) => {
                const qty = parseFloat(item.quantity) || 0;
                const maxQty = parseFloat(item.available_stock) || 0;
                if (qty > maxQty) newErrors[`quantity_${index}`] = translations?.quantityExceedsStock || 'Quantity exceeds available stock';
                if (qty <= 0) newErrors[`quantity_${index}`] = translations?.invalidQuantity || 'Please enter a valid quantity';
                const price = parseFloat(item.price) || 0;
                if (price <= 0) newErrors[`price_${index}`] = translations?.invalidPrice || 'Please enter a valid price';
            });
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (name === 'customer_name') searchCustomers(value);
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handlePaidAmountChange = (e) => {
        const value = e.target.value;
        setFormData(prev => ({ ...prev, paid_amount: value }));
        const paid = parseFloat(value) || 0;
        const grandTotal = parseFloat(formData.grand_total) || 0;
        const balance = grandTotal - paid;
        setFormData(prev => ({
            ...prev,
            balance: balance.toFixed(2),
            payment_status: balance <= 0 ? 'paid' : (paid > 0 ? 'partial' : 'unpaid')
        }));
    };

    // ============================================
    // RECEIPT FUNCTIONS
    // ============================================
    const escapeHtml = (text) => {
        if (!text) return '';
        return String(text).replace(/[&<>]/g, function (m) {
            if (m === '&') return '&amp;';
            if (m === '<') return '&lt;';
            if (m === '>') return '&gt;';
            return m;
        });
    };

    const formatReceiptCurrency = (amount) => {
        if (!amount && amount !== 0) return 'Rs. 0.00';
        return `Rs. ${parseFloat(amount).toFixed(2)}`;
    };

    const formatReceiptDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

   const handleDirectPrint = () => {
    if (formData.items.length === 0) {
        showToast('warning', 'No items to print receipt');
        return;
    }

    const printWindow = window.open('', '_blank');

    if (printWindow) {
        const shopName = businessInfo?.businessname || user?.fullname || currentShop?.shop_name || 'OurZest Store';
        const shopAddress = businessInfo?.businessaddress || '';
        const shopPhone = businessInfo?.phonenumber || '';
        const businessEmail = businessInfo?.email || '';
        const businessLogo = businessInfo?.bussinesslogo_url || businessInfo?.bussinesslogo || '';
        const customerName = formData.customer_name || 'Walk-in Customer';
        const customerPhone = formData.customer_phone || '';
        const customerAddress = formData.customer_address || '';
        const themeColor = currentThemeColor || '#6366f1';

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Invoice ${formData.bill_number}</title>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <style>
                    @page { size: auto; margin: 12mm; }
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif; background: white; color: #1f2937; font-size: 10pt; line-height: 1.4; }
                    .receipt-container { max-width: 100%; margin: 0 auto; padding: 0; }
                    .text-center { text-align: center; }
                    .text-right { text-align: right; }
                    .text-left { text-align: left; }
                    .font-bold { font-weight: bold; }
                    .header { display: flex; align-items: flex-start; justify-content: space-between; padding-bottom: 12px; border-bottom: 2px solid #e5e7eb; margin-bottom: 15px; }
                    .header-left { display: flex; align-items: center; gap: 15px; flex: 1; }
                    .header-logo-wrapper { flex-shrink: 0; }
                    .header-logo { max-height: 70px; max-width: 100px; width: auto; height: auto; object-fit: contain; display: block; }
                    .header-info { flex: 1; min-width: 0; }
                    .business-name { font-size: 18pt; font-weight: 700; color: #1f2937; letter-spacing: 0.5px; word-break: break-word; line-height: 1.2; }
                    .business-address { font-size: 9pt; color: #6b7280; margin-top: 2px; word-break: break-word; line-height: 1.4; }
                    .business-contact { display: flex; align-items: center; gap: 15px; flex-wrap: wrap; margin-top: 2px; font-size: 9pt; color: #6b7280; }
                    .header-right { text-align: right; flex-shrink: 0; margin-left: 20px; }
                    .header-right .receipt-label { font-size: 16pt; font-weight: 700; text-transform: uppercase; letter-spacing: 3px; color: #1f2937; }
                    .header-right .customer-name-label { font-size: 10pt; color: #4b5563; margin-top: 4px; font-weight: 500; }
                    .header-right .customer-name-label span { font-weight: 600; color: #1f2937; }
                    .bill-info { display: flex; justify-content: space-between; font-size: 10pt; margin: 4px 0; padding: 3px 0; flex-wrap: wrap; gap: 5px; }
                    .bill-info .label { font-weight: 600; color: #4b5563; }
                    .bill-info .value { font-weight: 500; }
                    .customer-info { margin: 10px 0; padding: 8px 12px; background: #f9fafb; border-radius: 6px; font-size: 10pt; border-left: 4px solid ${themeColor}; }
                    .customer-info .customer-phone { color: #4b5563; margin-top: 2px; }
                    .customer-info .customer-address { color: #6b7280; font-size: 9pt; margin-top: 2px; word-break: break-word; }
                    .items-table { width: 100%; border-collapse: collapse; margin: 12px 0; border: 1px solid #d1d5db; }
                    .items-table th { background: #f3f4f6; text-align: left; padding: 10px 8px; font-size: 9pt; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: #374151; border: 1px solid #d1d5db; }
                    .items-table td { padding: 8px 8px; font-size: 10pt; vertical-align: top; border: 1px solid #d1d5db; }
                    .items-table th:last-child, .items-table td:last-child { text-align: right; }
                    .items-table th:nth-child(1), .items-table td:nth-child(1) { text-align: center; }
                    .items-table th:nth-child(3), .items-table td:nth-child(3) { text-align: center; }
                    .items-table .product-name { font-weight: 500; word-break: break-word; }
                    .items-table .product-meta { font-size: 8pt; color: #6b7280; display: block; margin-top: 2px; }
                    .payment-section { display: flex; justify-content: flex-end; margin-top: 10px; padding-top: 5px; border-top: 1px solid #e5e7eb; }
                    .payment-box { width: 280px; }
                    .payment-row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 10.5pt; }
                    .payment-row .label { color: #4b5563; font-weight: 500; }
                    .payment-row .value { font-weight: 500; }
                    .payment-row.total { font-weight: 700; font-size: 13pt; padding-top: 8px; border-top: 2px solid #1f2937; margin-top: 4px; }
                    .payment-row.total .label { color: #1f2937; }
                    .payment-row.total .value { color: #059669; }
                    .payment-row .value.text-danger { color: #dc2626; }
                    .payment-row .value.text-primary { color: #3b82f6; }
                    .payment-row .value.text-warning { color: #d97706; }
                    .payment-row .value.text-success { color: #059669; }
                    .footer { text-align: center; font-size: 8pt; margin-top: 15px; padding-top: 10px; border-top: 1px solid #e5e7eb; color: #9ca3af; }
                    .footer .thankyou { font-size: 11pt; color: #4b5563; font-weight: 500; }
                    @media print { body { margin: 0; } .no-print { display: none; } }
                    @media print and (max-width: 160mm) {
                        body { font-size: 9pt; }
                        .business-name { font-size: 14pt; }
                        .header-right .receipt-label { font-size: 13pt; }
                        .items-table th, .items-table td { font-size: 8.5pt; padding: 6px 6px; }
                        .payment-box { width: 230px; }
                        .payment-row { font-size: 9pt; }
                        .payment-row.total { font-size: 11pt; }
                        .header-logo { max-height: 50px; max-width: 80px; }
                    }
                </style>
            </head>
            <body>
                <div class="receipt-container">
                    <div class="header">
                        <div class="header-left">
                            ${businessLogo ? `
                            <div class="header-logo-wrapper">
                                <img src="${businessLogo}" alt="${escapeHtml(shopName)}" class="header-logo" onerror="this.style.display='none'" />
                            </div>` : ''}
                            <div class="header-info">
                                <div class="business-name">${escapeHtml(shopName)}</div>
                                ${shopAddress ? `<div class="business-address">${escapeHtml(shopAddress)}</div>` : ''}
                                ${(shopPhone || businessEmail) ? `
                                <div class="business-contact">
                                    ${shopPhone ? `<span>📞 ${escapeHtml(shopPhone)}</span>` : ''}
                                    ${businessEmail ? `<span>✉ ${escapeHtml(businessEmail)}</span>` : ''}
                                </div>` : ''}
                            </div>
                        </div>
                        <div class="header-right">
                            <div class="receipt-label">${translations.receipt || 'INVOICE'}</div>
                            <div class="customer-name-label">${translations.customer || 'Customer'}: <span>${escapeHtml(customerName)}</span></div>
                        </div>
                    </div>
                    <div class="bill-info">
                        <span><span class="label">${translations.billNumber || 'Bill No'}:</span> <span class="value">#${formData.bill_number}</span></span>
                        <span><span class="label">${translations.date || 'Date'}:</span> <span class="value">${formatReceiptDate(formData.date)}</span></span>
                    </div>
                    ${(customerPhone || customerAddress) ? `
                    <div class="customer-info">
                        ${customerPhone ? `<div class="customer-phone">📞 ${escapeHtml(customerPhone)}</div>` : ''}
                        ${customerAddress ? `<div class="customer-address">📍 ${escapeHtml(customerAddress)}</div>` : ''}
                    </div>` : ''}
                    <table class="items-table">
                        <thead>
                            <tr>
                                <th style="width:5%;">#</th>
                                <th style="width:40%;">${translations.productName || 'Product'}</th>
                                <th style="width:15%;">${translations.quantity || 'Qty'}</th>
                                <th style="width:15%;">${translations.price || 'Price'}</th>
                                <th style="width:25%;">${translations.total || 'Total'}</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${formData.items.map((item, idx) => {
                                let unitLabel = '';
                                if (item.sellMethod === 'packing') {
                                    unitLabel = item.packing_type || 'Pack';
                                } else {
                                    unitLabel = item.sub_packing_type || item.unit_of_measure || 'Unit';
                                }
                                return `
                                <tr>
                                    <td>${idx + 1}</td>
                                    <td>
                                        <div class="product-name">
                                            ${escapeHtml(item.product_name || 'Product')}
                                        </div>
                                    </td>
                                    <td>${parseFloat(item.quantity).toFixed(0)} ${escapeHtml(unitLabel)}</td>
                                    <td>${parseFloat(item.price).toFixed(0)}</td>
                                    <td>${parseFloat(item.total || item.price * item.quantity).toFixed(0)}</td>
                                </tr>
                            `}).join('')}
                        </tbody>
                    </table>
                    <div class="payment-section">
                        <div class="payment-box">
                            <div class="payment-row">
                                <span class="label">${translations.subtotal || 'Subtotal'}</span>
                                <span class="value">${formatReceiptCurrency(formData.subtotal)}</span>
                            </div>
                            ${parseFloat(formData.tax) > 0 ? `
                            <div class="payment-row">
                                <span class="label">${translations.tax || 'Tax'}</span>
                                <span class="value">${formatReceiptCurrency(formData.tax)}</span>
                            </div>` : ''}
                            ${parseFloat(formData.discount) > 0 ? `
                            <div class="payment-row">
                                <span class="label">${translations.discount || 'Discount'}</span>
                                <span class="value text-danger">-${formatReceiptCurrency(formData.discount)}</span>
                            </div>` : ''}
                            <div class="payment-row total">
                                <span class="label">${translations.total || 'Total'}</span>
                                <span class="value">${formatReceiptCurrency(formData.grand_total)}</span>
                            </div>
                            ${parseFloat(formData.paid_amount) > 0 ? `
                            <div class="payment-row">
                                <span class="label">${translations.paidAmount || 'Paid'}</span>
                                <span class="value text-primary">${formatReceiptCurrency(formData.paid_amount)}</span>
                            </div>` : ''}
                            ${parseFloat(formData.balance) > 0 ? `
                            <div class="payment-row">
                                <span class="label">${translations.balance || 'Balance'}</span>
                                <span class="value text-warning">${formatReceiptCurrency(formData.balance)}</span>
                            </div>` : ''}
                            ${parseFloat(formData.balance) <= 0 && parseFloat(formData.paid_amount) > 0 ? `
                            <div class="payment-row">
                                <span class="label">${translations.balance || 'Balance'}</span>
                                <span class="value text-success">${formatReceiptCurrency(formData.balance)}</span>
                            </div>` : ''}
                        </div>
                    </div>
                </div>
                <script>
                window.onload = function() {
                    setTimeout(function() {
                        window.print();
                        window.onafterprint = function() { window.close(); };
                    }, 500);
                };
                </script>
            </body>
            </html>
        `);

        printWindow.document.close();
        printWindow.focus();
    }
};

    // ============================================
    // handleSubmit - FIXED: Proper quantity handling
    // ============================================
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSavingRef.current) {
            showToast('warning', 'Please wait, sale is already being processed...');
            return;
        }

        isSavingRef.current = true;
        if (submitButtonRef.current) {
            submitButtonRef.current.disabled = true;
        }

        if (!currentShop?.shop_id) {
            showToast('error', translations?.noShopSelected || 'No shop selected. Please select a shop first.');
            isSavingRef.current = false;
            if (submitButtonRef.current) submitButtonRef.current.disabled = false;
            return;
        }

        if (!validateForm()) {
            isSavingRef.current = false;
            if (submitButtonRef.current) submitButtonRef.current.disabled = false;
            return;
        }

        setSaving(true);

        try {
            const currentUsername = user?.username || '';
            if (!currentUsername) throw new Error("User not authenticated");

            const action = isEditMode ? 'edit' : 'add';
            
            const preparedItems = formData.items.map(item => {
                let finalQuantity = parseFloat(item.quantity);
                let sellMethod = item.sellMethod;
                let conversionRate = item.items_per_pack || 1;

                return {
                    product_id: item.product_id,
                    product_name: item.product_name,
                    product_code: item.product_code || '',
                    batch_no: item.batch_no,
                    barcode: item.barcode,
                    unit: item.display_unit || item.unit_of_measure,
                    quantity: finalQuantity,
                    display_quantity: parseFloat(item.quantity),
                    sell_method: sellMethod,
                    packing_type: item.packing_type,
                    sub_packing_type: item.sub_packing_type,
                    items_per_pack: conversionRate,
                    unit_of_measure: item.unit_of_measure,
                    price: parseFloat(item.price),
                    total: parseFloat(item.price) * parseFloat(item.quantity)
                };
            });

            const payload = {
                action: action,
                username: currentUsername,
                shop_id: currentShop.shop_id,
                ...formData,
                items: preparedItems
            };
            if (isEditMode && saleId) payload.id = saleId;

            const response = await fetch(SELL_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();

            if (data.status === 'success') {
                showToast('success', isEditMode ? (translations?.saleUpdated || 'Sale updated successfully!') : (translations?.saleCompleted || 'Sale completed successfully!'));

                if (!isEditMode) {
                    setFormData({
                        ...initialFormData,
                        date: new Date().toISOString().split('T')[0],
                        bill_number: data.data?.bill_number || '',
                        bill_number_int: data.data?.bill_number_int || 0
                    });
                    setProductSearchTerm('');
                    setErrors({});
                    getNextBillNumber();

                    setTimeout(() => {
                        if (customerNameRef.current) {
                            customerNameRef.current.focus();
                        }
                    }, 100);
                }

                if (onSuccess) onSuccess(data.data?.sell_id || saleId);

                setTimeout(() => {
                    if (data.data) {
                        setFormData(prev => ({
                            ...prev,
                            bill_number: data.data.bill_number || prev.bill_number,
                            date: data.data.date || prev.date,
                            subtotal: data.data.subtotal || prev.subtotal,
                            tax: data.data.tax || prev.tax,
                            discount: data.data.discount || prev.discount,
                            grand_total: data.data.grand_total || prev.grand_total,
                            paid_amount: data.data.paid_amount || prev.paid_amount,
                            balance: data.data.balance || prev.balance,
                            payment_method: data.data.payment_method || prev.payment_method,
                            customer_name: data.data.customer_name || prev.customer_name,
                            customer_phone: data.data.customer_phone || prev.customer_phone,
                            customer_address: data.data.customer_address || prev.customer_address
                        }));
                        setTimeout(handleDirectPrint, 500);
                    } else {
                        handleDirectPrint();
                    }
                }, 300);
            } else {
                showToast('error', data.message || (isEditMode ? translations?.failedUpdate : translations?.failedSave) || 'Failed to save sale');
            }
        } catch (error) {
            showToast('error', error.message || translations?.networkError || 'Network error occurred');
        } finally {
            isSavingRef.current = false;
            setSaving(false);
            setTimeout(() => {
                if (submitButtonRef.current) submitButtonRef.current.disabled = false;
            }, 2000);
        }
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`ozui-container ${isRTL ? 'ozui-rtl' : 'ozui-ltr'} ${darkMode ? 'ozui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />

            <div className="ozui-sticky-header">
                <div className="ozui-header-bg-animation">
                    <div className="gradient-orb orb-1"></div>
                    <div className="gradient-orb orb-2"></div>
                    <div className="gradient-orb orb-3"></div>
                    <div className="gradient-orb orb-4"></div>
                    <div className="gradient-orb orb-5"></div>
                </div>
                <div className="ozui-header-content">
                    <div className="ozui-header-left">
                        <div className="ozui-header-title">
                            <h1>{isEditMode ? (translations?.editSale || 'Edit Sale') : (translations?.newSale || 'New Sale')}</h1>
                            <p>{translations?.sellProducts || 'Sell Products'}</p>
                        </div>
                    </div>

                    <div className="ozui-header-right">
                        <div className="ozui-header-actions">
                            <button
                                className="ozui-action-btn clear-draft"
                                onClick={clearDraft}
                                disabled={saving}
                                title={translations?.clearForm || 'Clear Form'}
                            >
                                <FontAwesomeIcon icon={faTrashAlt} />
                                <span>{translations?.clear || 'Clear'}</span>
                            </button>

                            <button
                                className="ozui-action-btn save-draft"
                                onClick={saveDraft}
                                disabled={saving}
                                title={translations?.saveDraft || 'Save as Draft'}
                            >
                                <FontAwesomeIcon icon={faCopy} />
                                <span>{translations?.saveDraft || 'Save Draft'}</span>
                            </button>

                            <div className="ozui-drafts-dropdown-container" ref={draftsDropdownRef}>
                                <button
                                    className="ozui-action-btn drafts-btn"
                                    onClick={() => setShowDraftsList(!showDraftsList)}
                                    title={translations?.viewDrafts || 'View Drafts'}
                                >
                                    <FontAwesomeIcon icon={faFileAlt} />
                                    <span>{translations?.drafts || 'Drafts'}</span>
                                    {savedDrafts.length > 0 && <span className="drafts-badge">{savedDrafts.length}</span>}
                                </button>

                                {showDraftsList && (
                                    <div className="ozui-drafts-dropdown">
                                        <div className="drafts-header">
                                            <h4>{translations?.savedDrafts || 'Saved Drafts'}</h4>
                                            <button onClick={() => setShowDraftsList(false)}>×</button>
                                        </div>
                                        <div className="drafts-list">
                                            {savedDrafts.length === 0 ? (
                                                <div className="no-drafts">{translations?.noDraftsSaved || 'No drafts saved'}</div>
                                            ) : (
                                                savedDrafts.map(draft => (
                                                    <div key={draft.id} className="draft-item">
                                                        <div className="draft-info" onClick={() => loadDraft(draft)}>
                                                            <div className="draft-name">
                                                                {draft.bill_number || `Sale #${draft.id}`}
                                                            </div>
                                                            <div className="draft-details">
                                                                {draft.customer_name && <span>{draft.customer_name}</span>}
                                                                {draft.items && <span>{draft.items.length} items</span>}
                                                            </div>
                                                            <div className="draft-date">
                                                                {new Date(draft.savedAt).toLocaleDateString()}
                                                            </div>
                                                        </div>
                                                        <button
                                                            className="delete-draft-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                deleteDraft(draft.id);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </button>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="ozui-form">
                {/* Barcode Scanner Status Bar */}
                <div style={{
                    background: isScannerActive ? '#f0fdf4' : '#f9fafb',
                    border: `1px solid ${isScannerActive ? '#bbf7d0' : '#e5e7eb'}`,
                    borderRadius: '6px',
                    padding: '6px 12px',
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '0.85rem'
                }}>
                    <FontAwesomeIcon icon={faBarcode} style={{ color: isScannerActive ? '#10b981' : '#9ca3af' }} />
                    <span style={{ flex: 1, color: isScannerActive ? '#059669' : '#6b7280', fontWeight: 500 }}>
                        {isScannerActive ? (translations?.scannerReady || 'Scanner Ready') : 'Scanner Paused'}
                    </span>
                    <button
                        type="button"
                        onClick={() => setIsScannerActive(!isScannerActive)}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: isScannerActive ? '#059669' : '#6b7280',
                            fontSize: '1.1rem',
                            padding: '2px 6px'
                        }}
                    >
                        <FontAwesomeIcon icon={isScannerActive ? faToggleOn : faToggleOff} />
                    </button>
                </div>

                {/* Hidden Barcode Input */}
                <input
                    ref={barcodeInputRef}
                    type="text"
                    style={{ position: 'absolute', left: '-9999px', opacity: 0, width: '1px', height: '1px' }}
                    autoComplete="off"
                    tabIndex={-1}
                    onChange={() => {}}
                />

                <div className="ozui-form-row">
                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                id="bill_number"
                                value={formData.bill_number}
                                readOnly
                                placeholder=" "
                                className="ozui-readonly-input"
                            />
                            <label htmlFor="bill_number">
                                <FontAwesomeIcon icon={faFileInvoice} />
                                {translations?.billNumber || 'Bill Number'}
                            </label>
                        </div>
                    </div>

                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="date"
                                name="date"
                                id="date"
                                value={formData.date}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={saving}
                            />
                            <label htmlFor="date">
                                <FontAwesomeIcon icon={faCalendarAlt} />
                                {translations?.date || 'Date'}
                            </label>
                        </div>
                    </div>
                </div>

                <div className="ozui-form-row">
                    <div className="ozui-field" ref={customerSearchRef}>
                        <div className="ozui-input-wrapper">
                            <input
                                ref={customerNameRef}
                                type="text"
                                name="customer_name"
                                id="customer_name"
                                value={formData.customer_name}
                                onChange={handleChange}
                                onFocus={() => formData.customer_name.length >= 2 && setShowCustomerDropdown(true)}
                                placeholder=" "
                                disabled={saving}
                                autoComplete="off"
                            />
                            <label htmlFor="customer_name">
                                <FontAwesomeIcon icon={faUser} />
                                {translations?.customerName || 'Customer Name'}
                                <span>({translations?.optional || 'Optional'})</span>
                            </label>
                            {searchingCustomer && <FontAwesomeIcon icon={faSpinner} spin className="ozui-search-indicator" />}
                        </div>
                        {showCustomerDropdown && customerSearchResults.length > 0 && (
                            <div className="ozui-customer-dropdown">
                                {customerSearchResults.map(customer => (
                                    <div key={customer.customerid} className="ozui-dropdown-item" onClick={() => selectCustomer(customer)}>
                                        <div className="ozui-customer-name">{customer.customer_name}</div>
                                        {customer.phone && <div className="ozui-customer-phone"><FontAwesomeIcon icon={faPhone} /> {customer.phone}</div>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="tel"
                                name="customer_phone"
                                id="customer_phone"
                                value={formData.customer_phone}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={saving}
                            />
                            <label htmlFor="customer_phone">
                                <FontAwesomeIcon icon={faPhone} />
                                {translations?.customerPhone || 'Phone Number'}
                                <span>({translations?.optional || 'Optional'})</span>
                            </label>
                        </div>
                    </div>
                </div>

                <div className="ozui-form-row">
                    <div className="ozui-field" ref={productSearchRef}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                id="product_search"
                                value={productSearchTerm}
                                onChange={(e) => {
                                    setProductSearchTerm(e.target.value);
                                    searchProducts(e.target.value);
                                }}
                                onFocus={() => productSearchTerm.length >= 2 && setShowProductDropdown(true)}
                                placeholder=" "
                                disabled={saving}
                                autoComplete="off"
                            />
                            <label htmlFor="product_search">
                                <FontAwesomeIcon icon={faSearch} />
                                {translations?.searchProducts || 'Search Products'}
                            </label>
                            {searchingProducts && <FontAwesomeIcon icon={faSpinner} spin className="ozui-search-indicator" />}
                        </div>
                        {showProductDropdown && filteredProducts.length > 0 && (
                            <div className="ozui-product-dropdown">
                                {filteredProducts.map(product => (
                                    <div key={product.id} className="ozui-product-item" onClick={() => handleProductSelect(product)}>
                                        <div className="ozui-product-item-name">
                                            {product.product_name}
                                            {product.product_code && <span className="ozui-product-code-badge">{product.product_code}</span>}
                                        </div>
                                        <div className="ozui-product-item-details">
                                            {product.batch_no && <span><FontAwesomeIcon icon={faTag} /> Batch: {product.batch_no}</span>}
                                            {product.category && <span><FontAwesomeIcon icon={faBox} /> {product.category}</span>}
                                        </div>
                                        <div className="ozui-product-item-price-stock">
                                            <span className="ozui-product-price"><FontAwesomeIcon icon={faRupeeSign} /> {parseFloat(product.sell_price || 0).toFixed(2)}</span>
                                            <span className={`ozui-product-stock ${parseFloat(product.quantity) <= (product.quantity_alert || 5) ? 'ozui-low-stock' : ''}`}>
                                                Stock: {product.quantity} packs / {parseFloat(product.available_bulk).toFixed(2)} {product.unit_of_measure || 'units'}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {formData.items.length > 0 && (
                    <ItemsTable
                        items={formData.items}
                        onItemChange={handleItemChange}
                        onRemove={removeItem}
                        onAdd={addItem}
                        onToggleSellMethod={toggleSellMethod}
                        errors={errors}
                        disabled={saving}
                        translations={translations}
                        isRTL={isRTL}
                    />
                )}

                {formData.items.length > 0 && (
                    <div className="ozui-form-section">
                        <div className="ozui-calculations">
                            <div className="ozui-calculation-row">
                                <span className="ozui-calculation-label">{translations?.subtotal || 'Subtotal'}:</span>
                                <span className="ozui-calculation-value">Rs. {formData.subtotal}</span>
                            </div>
                            <div className="ozui-calculation-row">
                                <span className="ozui-calculation-label">{translations?.tax || 'Tax'}:</span>
                                <div className="ozui-input-wrapper ozui-small-input">
                                    <input type="number" name="tax" value={formData.tax} onChange={handleChange} step="0.01" min="0" placeholder="0.00" disabled={saving} />
                                </div>
                            </div>
                            <div className="ozui-calculation-row">
                                <span className="ozui-calculation-label">{translations?.discount || 'Discount'}:</span>
                                <div className="ozui-input-wrapper ozui-small-input">
                                    <input type="number" name="discount" value={formData.discount} onChange={handleChange} step="0.01" min="0" placeholder="0.00" disabled={saving} />
                                </div>
                            </div>
                            <div className="ozui-calculation-row ozui-grand-total">
                                <span className="ozui-calculation-label">{translations?.grandTotal || 'Grand Total'}:</span>
                                <span className="ozui-calculation-value">Rs. {formData.grand_total}</span>
                            </div>
                            <div className="ozui-calculation-row">
                                <span className="ozui-calculation-label">{translations?.paidAmount || 'Paid Amount'}:</span>
                                <div className="ozui-input-wrapper ozui-small-input">
                                    <input type="number" name="paid_amount" value={formData.paid_amount} onChange={handlePaidAmountChange} step="0.01" min="0" max={formData.grand_total} placeholder="0.00" disabled={saving} />
                                </div>
                            </div>
                            <div className="ozui-calculation-row">
                                <span className="ozui-calculation-label">{translations?.balance || 'Balance'}:</span>
                                <span className="ozui-calculation-value">Rs. {formData.balance}</span>
                            </div>
                        </div>
                    </div>
                )}

                <div className="ozui-form-actions">
                    {formData.items.length > 0 && (
                        <button
                            type="button"
                            className="ozserui-action-btn"
                            onClick={handleDirectPrint}
                            title={translations?.printReceipt || 'Print Receipt'}
                            style={{ background: 'var(--ozserui-primary)', color: 'white', padding: '0.5rem 1rem' }}
                        >
                            <FontAwesomeIcon icon={faReceipt} />
                            <span>{translations?.receipt || 'Receipt'}</span>
                        </button>
                    )}

                    <button
                        ref={submitButtonRef}
                        type="submit"
                        className="ozui-btn ozui-btn-primary"
                        disabled={saving || formData.items.length === 0}
                    >
                        {saving ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>{translations?.saving || 'Saving...'}</span>
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faSave} />
                                <span>{isEditMode ? (translations?.updateSale || 'Update Sale') : (translations?.completeSale || 'Complete Sale')}</span>
                            </>
                        )}
                    </button>
                </div>
            </form>

            {loading && (
                <div className="ozui-loading-overlay">
                    <div className="ozui-loading-content">
                        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                        <p>{translations?.loading || 'Loading...'}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Sell;